/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import { DarkTheme } from './dark-theme/dark-theme';
import { LightTheme } from './light-theme/light-theme';
import type { ColorTokens, Theme } from '../styles/types';

/**
 * The un-overridden colors a theme started from, which the v2 upgrade compares
 * against to tell an untouched default apart from a deliberate override.
 *
 * That is precisely the default theme's own `colors`, so this reads it rather
 * than rebuilding it: a second copy would be one more thing to keep in step
 * with the token modules, and it would cost every caller a map it may never
 * need.
 *
 * Selected from the theme itself, never from `ThemeProvider`'s `themeMode`
 * prop — that prop overrides `theme.themeMode`, so an app may render a
 * light-derived theme as `themeMode="dark"`. Measuring such a theme against
 * the dark defaults would mark every color as overridden and silently skip the
 * entire upgrade.
 *
 * Returns `undefined` for a theme of unrecognized lineage, which
 * `mergeV2Colors` treats as "no baseline available", skipping the
 * override-sensitive passes rather than guessing.
 */
export function getV1ColorBaseline(theme: Theme): ColorTokens | undefined {
  if (theme.name === LightTheme.name) return LightTheme.colors;
  if (theme.name === DarkTheme.name) return DarkTheme.colors;
  if (theme.themeMode === 'light') return LightTheme.colors;
  if (theme.themeMode === 'dark') return DarkTheme.colors;
  return undefined;
}